import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import { Avatar } from '../../components/ui/UserCell';
import { Field, Select, TextInput } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getActiveUser, updateProfile } from '../../actions/authAction';
import { ROLES } from './roles';
import CountryList from '../../static/country-list.json';
import './admin.css';

export default function Profile() {
  const dispatch = useDispatch();
  const { toast } = useOverlays();
  const activeUser = useSelector((s) => s.auth?.active_user?.data);

  const [form, setForm] = useState({ first_name: '', last_name: '', country: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!activeUser) return;
    setForm({
      first_name: activeUser.first_name || '',
      last_name: activeUser.last_name || '',
      country: activeUser.country || '',
      password: '',
    });
  }, [activeUser]);

  const countries = useMemo(
    () =>
      (Array.isArray(CountryList) ? CountryList : []).map((c) => ({
        value: c.name || c,
        label: c.name || c,
      })),
    []
  );

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const save = useCallback(async () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'Required';
    if (!form.last_name.trim()) next.last_name = 'Required';
    if (form.password && form.password.length < 8) next.password = 'Use at least 8 characters';
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    try {
      const token = JSON.parse(sessionStorage.getItem('auth'))?.accessToken;
      const payload = {
        id: activeUser?.id,
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        country: form.country,
      };
      // Only send a password when one was actually typed, so saving the name
      // does not silently reset it.
      if (form.password) payload.password = form.password;

      await updateProfile(dispatch, payload);
      await getActiveUser(dispatch);
      setForm((f) => ({ ...f, password: '' }));
      toast('Profile updated', { tone: 'success' });
    } catch {
      toast('Could not update your profile', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [form, activeUser, dispatch, toast]);

  const fullName = [activeUser?.first_name, activeUser?.last_name].filter(Boolean).join(' ');

  return (
    <>
      <PageHeader section="Admin" title="My profile" subtitle="Your details and sign-in credentials." />

      <div className="sv-screen-body">
        <div className="sv-invite">
          <section className="sv-card sv-invite__card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <Avatar name={fullName || 'Admin'} seed={activeUser?.id} size={52} fontSize={20} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{fullName || 'Admin'}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{activeUser?.email}</div>
              </div>
            </div>

            <div className="sv-invite__grid">
              <Field label="First name" required error={errors.first_name}>
                <TextInput value={form.first_name} onChange={set('first_name')} invalid={!!errors.first_name} />
              </Field>
              <Field label="Last name" required error={errors.last_name}>
                <TextInput value={form.last_name} onChange={set('last_name')} invalid={!!errors.last_name} />
              </Field>
              <Field label="Country" className="sv-span-2">
                <Select options={countries} placeholder="Select" value={form.country} onChange={set('country')} />
              </Field>
            </div>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Password</h2>
            <Field
              label="New password"
              error={errors.password}
              hint="Leave empty to keep your current password"
            >
              <TextInput
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={set('password')}
                invalid={!!errors.password}
              />
            </Field>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Your access</h2>
            <div className="sv-role-chips">
              {(activeUser?.roles || []).map((role) => (
                <span className="sv-role-chip" key={role}>
                  {ROLES.find((r) => r.value === role)?.label || role}
                </span>
              ))}
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 0, marginTop: 12 }}>
              Roles are managed by a super admin from the Team screen.
            </p>
          </section>

          <div className="sv-invite__foot">
            <Button variant="primary" onClick={save} loading={busy}>
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
